import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { api, eur, STATUS_LABEL } from "@/lib/api";
import { OrderReviews } from "@/components/OrderReviews";

const STEPS = ["ricevuto", "in_lavorazione", "spedito", "consegnato"];

const Timeline = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <div className="grid grid-cols-4 border border-[#0F0F0F]" data-testid="order-timeline">
      {STEPS.map((s, i) => (
        <div key={s} className={`p-3 md:p-4 border-r last:border-r-0 border-[#0F0F0F] ${i <= idx ? "bg-[#0F0F0F] text-white" : "bg-white"}`}>
          <span className="tag block">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-xs md:text-sm">{STATUS_LABEL[s]}</span>
        </div>
      ))}
    </div>
  );
};

export function TrackForm() {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  return (
    <main className="max-w-xl mx-auto px-5 py-16" data-testid="track-page">
      <span className="tag text-[#555]">Servizio clienti</span>
      <h1 className="font-display font-black text-4xl tracking-tight mt-1 mb-8">Traccia il tuo ordine</h1>
      <form onSubmit={(e) => { e.preventDefault(); nav(`/ordine/${code.trim().toUpperCase()}`); }} className="flex gap-2">
        <input data-testid="track-code-input" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="MK-XXXXXXXX" className="field font-mono" />
        <button data-testid="track-submit-button" className="btn-primary px-6">Cerca</button>
      </form>
    </main>
  );
}

export default function OrderPage() {
  const { code } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/orders/track/${code}`).then((r) => setO(r.data)).catch(() => setErr(true)); }, [code]);

  if (err) return <p className="p-10" data-testid="order-not-found">Ordine non trovato.</p>;
  if (!o) return <p className="p-10 tag">Caricamento...</p>;

  return (
    <main className="max-w-3xl mx-auto px-5 py-12 space-y-8" data-testid="order-page">
      <div className="flex items-start gap-4">
        <CheckCircle size={40} strokeWidth={1.5} className="text-[#00A651] shrink-0" />
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Grazie, {o.customer.name.split(" ")[0]}!</h1>
          <p className="text-[#555] mt-1">Il tuo ordine è confermato. Lo riceverai direttamente a {o.customer.city}.</p>
        </div>
      </div>
      <div className="border border-[#0F0F0F] bg-white p-5 flex justify-between items-center">
        <span className="tag">Codice ordine</span>
        <span className="font-mono text-lg" data-testid="order-code">{o.code}</span>
      </div>
      <Timeline status={o.status} />
      <p className="tag" data-testid="order-status">Stato: {STATUS_LABEL[o.status]}</p>
      {o.tracking_number && (
        <p className="text-sm" data-testid="order-tracking">
          Tracking spedizione: <span className="font-mono">{o.tracking_number}</span>
          {o.tracking_url && <> · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="underline hover:text-[#FF3333]">Segui il pacco</a></>}
        </p>
      )}
      <div className="border border-[#0F0F0F] bg-white divide-y divide-[#0F0F0F]/20">
        {o.items.map((i, k) => (
          <div key={k} className="p-4 flex gap-3 items-center">
            <img src={i.image} alt="" className="w-14 h-14 object-cover border border-[#0F0F0F]" />
            <p className="flex-1 text-sm">{i.title} <span className="font-mono text-[#555]">× {i.qty}</span></p>
            <span className="font-mono text-sm">{eur(i.unit_price * i.qty)}</span>
          </div>
        ))}
        <div className="p-4 flex justify-between items-end">
          <span className="tag">Totale pagato</span>
          <span className="font-display font-black text-3xl" data-testid="order-total">{eur(o.total)}</span>
        </div>
      </div>
      {o.status === "consegnato" && <OrderReviews order={o} />}
    </main>
  );
}
